import {
  type Dispatch,
  type SetStateAction,
  useState,
  type ComponentProps,
  useCallback,
  type FC,
} from 'react';
import { Linking } from 'react-native';
import { useQuery } from '@apollo/client';
import {
  Modal,
  TextInput,
  View,
  Text,
  Radio,
  Button,
  Alert,
  SearchUserQueryEnum,
  SearchUserQueryTypes,
  Spinner,
  Column,
  Row,
  Colors,
} from '@rivallapp/volosports-components';
import SEARCH_USER_QUERY, { type UserInfoInput, type UserInfoResult } from './SEARCH_USER_QUERY';
import { Error } from '../../../components';
import useDebounce from '../../../hooks/useDebounce';

const searchTypes = Object.values(SearchUserQueryTypes);
interface SearchAndAddUsersProps {
  headerText: string;
  descriptionText: string;
  userButtonPressText: string;
  updating: boolean;
  addUser: (userId: string) => Promise<void>;

  _header?: ComponentProps<typeof Text>;
  _button?: ComponentProps<typeof Button>;

  // Optional Alert state
  showAlert?: boolean;
  alertType?: string;
  alertMessage?: string;
  setShowAlert?: Dispatch<SetStateAction<boolean>>;
}

export const SearchAndAddUsers: FC<SearchAndAddUsersProps> = ({
  headerText,
  descriptionText,
  userButtonPressText,
  addUser,
  updating,
  _header,
  _button,
  showAlert = false,
  alertType = 'error',
  alertMessage = '',
  setShowAlert = () => {},
}) => {
  const [value, setValue] = useState('');
  const [which, setWhich] = useState<SearchUserQueryEnum>(SearchUserQueryEnum.EMAIL);

  const search = useDebounce(value.toLowerCase());

  const {
    data: userData,
    loading,
    error,
  } = useQuery<UserInfoResult, UserInfoInput>(SEARCH_USER_QUERY, {
    skip: search === '',
    variables: {
      input: {
        search,
        which,
      },
    },
  });

  const SearchResult = useCallback(() => {
    if (loading) return <Spinner mt={4} />;
    if (error) return <Error error={error} />;

    if (!userData?.getUserByInfo) {
      return (
        <Text m={4} fontSize="sm" textAlign="center">
          No users matching criteria.
        </Text>
      );
    }

    const { firstName, lastName, email, gender, _id: userId } = userData.getUserByInfo;
    return (
      <Row justifyContent="center" alignItems="center" p={2} mt={1} space={1} flexWrap="wrap">
        <Text fontSize="sm" textAlign="center">
          {lastName}, {firstName}{' '}
          <Text fontSize="sm" color={Colors.greys.medium}>
            ({gender})
          </Text>
        </Text>
        <Button
          variant="link"
          _text={{ fontSize: 'sm' }}
          onPress={() => Linking.openURL(`mailto:${email}`)}
        >
          {email}
        </Button>
        <Button
          height="36px"
          borderRadius="full"
          backgroundColor="#f5a41d"
          isLoading={updating}
          isLoadingText="Updating..."
          onPress={() => addUser(userId)}
          _text={{ fontSize: 'sm', fontWeight: 'normal' }}
          {..._button}
        >
          {userButtonPressText}
        </Button>
      </Row>
    );
  }, [loading, error, userData, updating, _button, userButtonPressText, addUser]);

  return (
    <View>
      <Column alignItems="center">
        <Alert
          status={alertType}
          message={alertMessage}
          showAlert={showAlert}
          setShowAlert={setShowAlert}
        />
        <Text fontSize="lg" fontWeight="bold" textAlign="center" {..._header}>
          {headerText}
        </Text>
        <Text m={3} fontSize="sm" textAlign="center">
          {descriptionText}
        </Text>
        <Row pt={2} pb={3} alignItems="center" justifyContent="center" flexWrap="wrap">
          <Text fontSize="sm" bold mx={2}>
            Search by:
          </Text>
          <Radio.Group
            name="searchType"
            flexDirection="row"
            value={which}
            onChange={value => setWhich(value as SearchUserQueryEnum)}
          >
            {searchTypes.map(({ label, value }) => (
              <Radio
                key={value}
                size="sm"
                value={value}
                _text={{ fontWeight: 'bold', marginRight: 3 }}
              >
                {label}
              </Radio>
            ))}
          </Radio.Group>
        </Row>
        <TextInput
          w={400}
          value={value}
          fontSize="sm"
          variant="rounded"
          onChangeText={setValue}
          maxW="100%"
          placeholder={SearchUserQueryTypes[which].label}
        />
        {search && <SearchResult />}
      </Column>
    </View>
  );
};

type SearchAndAddUsersModalProps = ComponentProps<typeof Modal> & SearchAndAddUsersProps;

export const SearchAndAddUsersModal: FC<SearchAndAddUsersModalProps> = ({ onClose, ...props }) => (
  <Modal useRNModal onClose={onClose} size="full" {...props}>
    <Modal.Content maxWidth={680} minHeight={260} padding={30} borderRadius="md">
      <Modal.CloseButton />
      <SearchAndAddUsers {...props} />
    </Modal.Content>
  </Modal>
);

export default SearchAndAddUsers;
